// Get the API URL from environment or default to localhost
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

// Domain Configuration
const DOMAIN = 'wmart.in';

// Store for generated emails to prevent URL manipulation
const STORAGE_KEY = 'securefakemail_generated_emails';

// Function to get generated emails from storage
function getGeneratedEmails(): string[] {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored ? JSON.parse(stored) : [];
}

// Function to save a generated email to storage
function saveGeneratedEmail(email: string): void {
    const emails = getGeneratedEmails();
    if (!emails.includes(email)) {
        emails.push(email);
        localStorage.setItem(STORAGE_KEY, JSON.stringify(emails));
    }
}

// Function to validate if an email was properly generated
export function isValidGeneratedEmail(email: string): boolean {
    return getGeneratedEmails().includes(email);
}

export interface EmailAttachment {
    id?: string;
    filename?: string;
    contentType?: string;
    size?: number;
    inline?: boolean;
    contentId?: string; // CID for inline images
    data?: string; // base64 body (optional; API dependent)
    dataUrl?: string; // prebuilt data URL
    downloadUrl?: string; // server URL to download the attachment
}

export interface Email {
    id: string;
    to: string;
    from: string;
    subject: string;
    content: string; // Plain text or raw fallback
    timestamp: string;
    read: boolean;
    important?: boolean;
    // Some backends provide attachment count; keep it backward-compatible
    attachments?: number;
    // Preferred: sanitized/parsed HTML provided by the backend (if available)
    html?: string;
    // Optional: full attachment metadata (if available)
    attachmentsList?: EmailAttachment[];
}

export interface GeneratedEmail {
    email: string;
    remainingEmails: number;
    // Optional fields that may not be present in the response
    created?: string;
    expiresIn?: string;
    domain?: string;
    instructions?: string;
}

// Validate email domain
function validateEmailDomain(email: string): boolean {
    return email.endsWith(`@${DOMAIN}`);
}

export const emailService = {
    async generateEmail(signal?: AbortSignal): Promise<GeneratedEmail> {
        try {
            const response = await fetch(`${API_URL}/email/generate`, {
                method: 'POST',
                signal
            });
            if (!response.ok) {
                throw new Error('Failed to generate email');
            }
            const data = await response.json();
            
            // Validate the received email
            if (!validateEmailDomain(data.email)) {
                throw new Error('Invalid email domain received from server');
            }
            
            // Save the generated email to prevent URL manipulation
            saveGeneratedEmail(data.email);
            
            // Return the data directly since it matches our interface
            return data;
        } catch (error) {
            console.error('Error generating email:', error);
            throw error;
        }
    },

    async getEmails(address: string): Promise<Email[]> {
        try {
            // Validate if this email was properly generated
            if (!validateEmailDomain(address) || !isValidGeneratedEmail(address)) {
                console.error('Access attempt to unauthorized email:', address);
                return [];
            }

            const response = await fetch(`${API_URL}/email/${address}`);
            if (!response.ok) {
                throw new Error('Failed to fetch emails');
            }
            return await response.json();
        } catch (error) {
            console.error('Error fetching emails:', error);
            return [];
        }
    },

    async markAsRead(address: string, emailId: string): Promise<void> {
        try {
            // Validate if this email was properly generated
            if (!validateEmailDomain(address) || !isValidGeneratedEmail(address)) {
                throw new Error('Unauthorized access to email address');
            }

            const response = await fetch(`${API_URL}/email/${address}/read/${emailId}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                },
            });
            if (!response.ok) {
                throw new Error('Failed to mark email as read');
            }
        } catch (error) {
            console.error('Error marking email as read:', error);
            throw error;
        }
    },

    async deleteEmail(address: string, emailId: string): Promise<void> {
        try {
            // Validate if this email was properly generated
            if (!validateEmailDomain(address) || !isValidGeneratedEmail(address)) {
                throw new Error('Unauthorized access to email address');
            }

            const response = await fetch(`${API_URL}/email/${address}/${emailId}`, {
                method: 'DELETE',
            });
            if (!response.ok) {
                throw new Error('Failed to delete email');
            }
        } catch (error) {
            console.error('Error deleting email:', error);
            throw error;
        }
    },

    async getServerStatus(): Promise<{status: string; totalEmails: number; activeAddresses: number; domain: string}> {
        try {
            const response = await fetch(`${API_URL}/status`);
            if (!response.ok) {
                throw new Error('Failed to get server status');
            }
            return await response.json();
        } catch (error) {
            console.error('Error getting server status:', error);
            return {
                status: 'error',
                totalEmails: 0,
                activeAddresses: 0,
                domain: 'worldmart.in'
            };
        }
    },

    async toggleImportant(address: string, emailId: string): Promise<void> {
        try {
            // Validate if this email was properly generated
            if (!validateEmailDomain(address) || !isValidGeneratedEmail(address)) {
                throw new Error('Unauthorized access to email address');
            }

            const response = await fetch(`${API_URL}/email/${address}/important/${emailId}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                },
            });
            if (!response.ok) {
                throw new Error('Failed to toggle important flag');
            }
        } catch (error) {
            console.error('Error toggling important flag:', error);
            throw error;
        }
    }
}; 